import LoadableStore, { LoadingState } from '@migrate/stores/LoadableStore'
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx'
import {
  type Enablements,
  type IlmPermission,
  type Scope,
  ScopedEnablementQueries,
  ScopedEnablements,
} from '@portal/pkg-authorization'
import { hashKey } from '@tanstack/react-query'
import queryClient from '@config/QueryClient'

const EMPTY = ScopedEnablements.empty()

/**
 * An observable view of the same scoped-enablements query the React hooks read, so a refetch
 * anywhere updates both. Stores have no ambient scope: anything gated on one Entity Type asks
 * {@link enablementsFor} with that type's scope.
 */
export default class EnablementStore extends LoadableStore {
  public static readonly STORE_NAME = 'enablementStore'

  @observable.ref private scopedEnablements: ScopedEnablements = EMPTY

  private unsubscribe?: () => void

  constructor() {
    super()
    makeObservable(this)
  }

  /** Across every scope the caller reaches; only for questions with no scope context. */
  public hasStandardEnablement(target: string, permission: IlmPermission) {
    return this.enablements.hasStandardEnablement(target, permission)
  }

  /** Across every scope the caller reaches; only for questions with no scope context. */
  public hasQualifiedEnablement(target: string, permission: IlmPermission, qualifier: string) {
    return this.enablements.hasQualifiedEnablement(target, permission, qualifier)
  }

  /** Across every scope the caller reaches; only for questions with no scope context. */
  @computed
  get enablements(): Enablements {
    return this.loaded.global()
  }

  /** Within one scope; fails closed for a scope the caller does not reach. */
  public enablementsFor(scope: Scope): Enablements {
    return this.loaded.for(scope)
  }

  /** The whole answer set, for helpers that ask about many scopes, such as filtering a type list. */
  public get scoped(): ScopedEnablements {
    return this.loaded
  }

  private get loaded() {
    return this.state === LoadingState.LOADED ? this.scopedEnablements : EMPTY
  }

  @action
  public async load(): Promise<void> {
    this.state = LoadingState.LOADING
    const query = ScopedEnablementQueries.scopedEnablements()
    const result = await queryClient.ensureQueryData(query)
    this.unsubscribe?.()
    this.unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const data: unknown = event.query.state.data
      if (
        event.type === 'updated' &&
        event.query.queryHash === hashKey(query.queryKey) &&
        data instanceof ScopedEnablements
      ) {
        runInAction(() => {
          this.scopedEnablements = data
        })
      }
    })
    runInAction(() => {
      this.scopedEnablements = result
      this.state = LoadingState.LOADED
    })
  }

  @override
  async unload(): Promise<void> {
    this.unsubscribe?.()
    this.unsubscribe = undefined
    this.scopedEnablements = EMPTY
    await super.unload()
  }
}
